/**
 * App-level hook that syncs RevenueCat subscription after IOSFlow purchase.
 * 
 * With anonymous auth, the user already has a real Supabase ID at purchase time,
 * so no anon→real transfer is needed. This hook handles edge cases where
 * subscription sync didn't complete during IOSFlow (e.g., app was backgrounded).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { isDespiaIOS } from '@/hooks/use-despia';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export function useRevenueCatLinking() {
  const { user } = useAuth();
  const hasAttempted = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user || hasAttempted.current) return;
    if (!isDespiaIOS()) return;

    // Check if there's a pending sync that IOSFlow didn't complete
    const pendingLink = localStorage.getItem('pending_revenuecat_link');
    if (!pendingLink) return;

    hasAttempted.current = true;

    const syncSubscription = async () => {
      console.log('[RC Linking] Syncing subscription for user:', user.id);

      try {
        // Clean up flag immediately
        localStorage.removeItem('pending_revenuecat_link');

        // Sync subscription via edge function (with retries)
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const { data, error } = await supabase.functions.invoke('sync-revenuecat-subscription');
            if (!error && data?.synced) {
              console.log('[RC Linking] Subscription synced successfully');
              queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
              return;
            }
            console.log(`[RC Linking] Sync attempt ${attempt}:`, data || error);
          } catch (e) {
            console.warn(`[RC Linking] Sync attempt ${attempt} failed:`, e);
          }
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        console.warn('[RC Linking] All sync attempts exhausted');
      } catch (err) {
        console.error('[RC Linking] Sync failed:', err);
        hasAttempted.current = false;
      }
    };

    const timer = setTimeout(syncSubscription, 500);
    return () => clearTimeout(timer);
  }, [user, queryClient]);
}
